import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api, money } from "@/lib/api";
import { Star, MapPin, MagnifyingGlass, Lightning, Buildings } from "@phosphor-icons/react";
import { DEFAULT_HOTEL_IMAGE } from "@/lib/api";

function HotelCard({ h }) {
  return (
    <Link
      to={`/hotels/${h.id}`}
      data-testid={`hotel-card-${h.id}`}
      className="rb-card rb-card-hover flex flex-col group"
    >
      <div className="relative h-48 overflow-hidden">
        <img
          src={h.images?.[0] || DEFAULT_HOTEL_IMAGE}
          alt={h.name}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          style={{ transition: "transform 0.5s ease" }}
        />
        {h.premium && (
          <span className="absolute top-3 left-3 inline-flex items-center gap-1 bg-[#002FA7] text-white px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider">
            <Lightning size={12} weight="fill" /> Premium
          </span>
        )}
        <span className="absolute top-3 right-3 bg-white/90 backdrop-blur px-2 py-1 text-xs font-semibold text-[#111827] flex items-center gap-1">
          <Star size={13} weight="fill" color="#D97706" /> {h.star_rating}
        </span>
      </div>
      <div className="p-5 flex flex-col flex-1">
        <span className="overline text-[#4B5563]">{h.brand}</span>
        <h3 className="font-display font-bold text-lg text-[#111827] mt-1 leading-tight">{h.name}</h3>
        <div className="flex items-center gap-1 text-sm text-[#4B5563] mt-1.5">
          <MapPin size={14} weight="fill" /> {h.city}, {h.state}
        </div>
        <div className="flex flex-wrap gap-1.5 mt-3">
          {h.amenities?.slice(0, 3).map((a) => (
            <span key={a} className="text-[11px] bg-[#F3F4F6] text-[#4B5563] px-2 py-0.5">{a}</span>
          ))}
        </div>
        <div className="mt-auto pt-4 flex items-end justify-between border-t border-[#E5E7EB] mt-4">
          <div>
            <span className="overline text-[#4B5563]">From</span>
            <div className="font-display font-black text-2xl text-[#111827]">
              {money(h.base_rate)}<span className="text-sm font-mono text-[#4B5563]">/nt</span>
            </div>
          </div>
          <span className="text-sm font-semibold text-[#002FA7] group-hover:underline">Negotiate →</span>
        </div>
      </div>
    </Link>
  );
}

export default function Marketplace() {
  const [q, setQ] = useState("");
  const [city, setCity] = useState("all");
  const [amenity, setAmenity] = useState("all");
  const [sort, setSort] = useState("premium");

  const { data: cities = [] } = useQuery({ queryKey: ["cities"], queryFn: api.cities });
  const { data: hotels = [], isLoading } = useQuery({
    queryKey: ["hotels", q, city, amenity, sort],
    queryFn: () => api.hotels({ q, city, amenity, sort }),
  });

  const amenities = ["Free Wi-Fi", "Breakfast", "Parking", "Pool", "Fitness Center", "Pet Friendly", "Airport Shuttle", "Business Center"];

  return (
    <div className="max-w-7xl mx-auto px-5 md:px-8 py-10">
      <div className="flex items-center gap-2 text-[#4B5563] text-sm mb-3">
        <Buildings size={16} weight="bold" /> <span className="overline">Marketplace</span>
      </div>
      <h1 className="font-display font-black text-4xl md:text-5xl text-[#111827]">
        Find your next room block.
      </h1>
      <p className="text-[#4B5563] mt-3 max-w-xl">
        Search hotels across the pilot region and start a direct negotiation with the owner.
      </p>

      {/* filters */}
      <div className="grid md:grid-cols-12 gap-3 mt-8 bg-white border border-[#E5E7EB] p-4">
        <div className="md:col-span-5 relative">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
          <input
            data-testid="search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search hotel, city or brand…"
            className="w-full pl-10 pr-3 py-2.5 border border-[#E5E7EB] text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
          />
        </div>
        <select data-testid="filter-city" value={city} onChange={(e) => setCity(e.target.value)}
          className="md:col-span-3 border border-[#E5E7EB] px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] bg-white">
          <option value="all">All cities</option>
          {cities.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select data-testid="filter-amenity" value={amenity} onChange={(e) => setAmenity(e.target.value)}
          className="md:col-span-2 border border-[#E5E7EB] px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] bg-white">
          <option value="all">Amenities</option>
          {amenities.map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
        <select data-testid="filter-sort" value={sort} onChange={(e) => setSort(e.target.value)}
          className="md:col-span-2 border border-[#E5E7EB] px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] bg-white">
          <option value="premium">Featured</option>
          <option value="price_asc">Price ↑</option>
          <option value="price_desc">Price ↓</option>
          <option value="rating">Rating</option>
        </select>
      </div>

      <div className="flex items-center justify-between mt-6 mb-4">
        <span className="text-sm text-[#4B5563]" data-testid="results-count">
          {isLoading ? "Loading…" : `${hotels.length} hotels`}
        </span>
      </div>

      {hotels.length === 0 && !isLoading ? (
        <div className="text-center py-24 text-[#4B5563]">No hotels match your filters.</div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {hotels.map((h) => <HotelCard key={h.id} h={h} />)}
        </div>
      )}
    </div>
  );
}
